import { createClient, SupabaseClient } from '@supabase/supabase-js'

/**
 * Development Issues Service
 * Handles all operations for the development tracker
 */

// Create a server-side client for server operations
function getServerClient(): SupabaseClient {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const serviceRoleKey = process.env.SUPABASE_SERVICE_KEY

  if (!supabaseUrl || !serviceRoleKey) {
    throw new Error('Missing Supabase environment variables')
  }

  return createClient(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  })
}

interface Attachment {
  url: string
  name: string
  type: 'image' | 'video'
  uploadedBy: string
  uploadedAt: string
}

interface Note {
  author: string
  text: string
  createdAt: string
  attachments?: Attachment[]
}

interface Upvoter {
  id: string
  name: string
  votedAt: string
}

export interface DevelopmentIssue {
  id: string
  title: string
  description: string
  category: 'bug' | 'feature' | 'improvement'
  status: 'backlog' | 'needs_info' | 'in_progress' | 'done'
  priority: 'critical' | 'high' | 'medium' | 'low'
  reportedBy?: string | null
  proId?: string | null
  assignedTo?: string | null
  createdAt: string
  updatedAt: string
  completedAt?: string | null
  tags: string[]
  upvotedBy: Upvoter[]
  notes: Note[]
  attachments: Attachment[]
}

export interface CreateIssueInput {
  id: string
  title: string
  description: string
  category: 'bug' | 'feature' | 'improvement'
  priority: 'critical' | 'high' | 'medium' | 'low'
  tags: string[]
  reportedBy: string
  proId: string
  attachments?: Attachment[]
}

export interface UpdateIssueInput {
  upvotedBy?: Upvoter[]
  status?: 'backlog' | 'needs_info' | 'in_progress' | 'done'
  completedAt?: string | null
  notes?: Note[]
  updatedAt: string
}

export const developmentIssuesService = {
  /**
   * Get all development issues
   */
  async getAll(client?: SupabaseClient): Promise<DevelopmentIssue[]> {
    const supabase = client || getServerClient()

    try {
      const { data, error } = await supabase
        .from('development_issues')
        .select('*')
        .order('createdAt', { ascending: false })

      if (error) {
        console.error('Error fetching development issues:', error)
        throw error
      }

      return data || []
    } catch (err) {
      console.error('Error in getAll:', err)
      throw err
    }
  },

  /**
   * Create a new development issue
   */
  async create(input: CreateIssueInput, client?: SupabaseClient): Promise<DevelopmentIssue> {
    const supabase = client || getServerClient()

    try {
      const now = new Date().toISOString()

      const { data, error } = await supabase
        .from('development_issues')
        .insert({
          id: input.id,
          title: input.title,
          description: input.description,
          category: input.category.toUpperCase(),
          status: 'BACKLOG',
          priority: input.priority.toUpperCase(),
          tags: input.tags,
          reportedBy: input.reportedBy,
          proId: input.proId,
          attachments: input.attachments || [],
          upvotedBy: [],
          notes: [],
          createdAt: now,
          updatedAt: now
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating development issue:', error)
        throw error
      }

      return data
    } catch (err) {
      console.error('Error in create:', err)
      throw err
    }
  },

  /**
   * Update a development issue
   */
  async update(id: string, input: UpdateIssueInput, client?: SupabaseClient): Promise<DevelopmentIssue> {
    const supabase = client || getServerClient()

    try {
      // Convert status to uppercase for database if provided
      const updateData: any = {
        ...input,
        updatedAt: new Date().toISOString()
      }

      if (input.status) {
        updateData.status = input.status.toUpperCase()
      }

      const { data, error } = await supabase
        .from('development_issues')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('Error updating development issue:', error)
        throw error
      }

      return data
    } catch (err) {
      console.error('Error in update:', err)
      throw err
    }
  },

  /**
   * Get therapist info by ID
   */
  async getTherapistById(proId: string, client?: SupabaseClient): Promise<{ email: string | null; firstName: string | null; lastName: string | null } | null> {
    const supabase = client || getServerClient()

    try {
      const { data, error } = await supabase
        .from('pros')
        .select('email, firstName, lastName')
        .eq('id', proId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching therapist:', error)
        return null
      }

      return data
    } catch (err) {
      console.error('Error in getTherapistById:', err)
      return null
    }
  },

  /**
   * Get multiple therapists by IDs
   */
  async getTherapistsByIds(proIds: string[], client?: SupabaseClient): Promise<Array<{ email: string | null; id: string }>> {
    const supabase = client || getServerClient()

    try {
      const { data, error } = await supabase
        .from('pros')
        .select('email, id')
        .in('id', proIds)

      if (error) {
        console.error('Error fetching therapists:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error in getTherapistsByIds:', err)
      return []
    }
  }
}
